/**
 * PHARMAKON — Fournisseur ERP · NotificationDrawer (Module 00, Appendix E.9)
 *
 * Per-staff + org-broadcast notification feed in a Drawer. Skeleton on load,
 * French empty state, inline error with retry. Rows carry a type icon, title,
 * message, relative time and a HIGH-priority accent; clicking a row marks it
 * read and deep-links via action_url. Composed from ERP V3 primitives + tokens.
 */
import * as React from "react";
import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import {
  Drawer,
  Button,
  Icon,
  Skeleton,
  EmptyState,
  ErrorState,
} from "@/components/ui/primitives";
import { fmtRelTime } from "@/config/locale.config.js";
import { notifTypeMeta, NOTIF_PRIORITY_TONE } from "@/config/fournisseur.constants.js";
import { useFournisseurNotifStore } from "@/stores/fournisseur/fournisseurNotifStore.js";

const TONE_TEXT = {
  neutral: "text-text-muted",
  accent: "text-accent",
  success: "text-success",
  warning: "text-warning",
  danger: "text-danger",
  info: "text-info",
};
const TONE_BORDER = {
  neutral: "border-l-border-default",
  accent: "border-l-accent",
  success: "border-l-success",
  warning: "border-l-warning",
  danger: "border-l-danger",
  info: "border-l-info",
};

function NotifRowSkeleton() {
  return (
    <li className="px-5 py-3 border-b border-border-subtle">
      <div className="flex items-start gap-3">
        <Skeleton w={16} h={16} className="rounded" />
        <div className="flex-1">
          <Skeleton w="60%" h={13} className="mb-1.5" />
          <Skeleton w="90%" h={11} />
        </div>
      </div>
    </li>
  );
}

export default function NotificationDrawer() {
  const navigate = useNavigate();
  const open = useFournisseurNotifStore((s) => s.drawerOpen);
  const close = useFournisseurNotifStore((s) => s.closeDrawer);
  const items = useFournisseurNotifStore((s) => s.items);
  const loading = useFournisseurNotifStore((s) => s.loading);
  const error = useFournisseurNotifStore((s) => s.error);
  const unread = useFournisseurNotifStore((s) => s.unread);
  const markRead = useFournisseurNotifStore((s) => s.markRead);
  const markAllRead = useFournisseurNotifStore((s) => s.markAllRead);
  const fetchList = useFournisseurNotifStore((s) => s.fetchList);

  const onRowClick = (n) => {
    if (!n.lue) markRead(n.id);
    if (n.action_url) {
      navigate(n.action_url);
      close();
    }
  };

  return (
    <Drawer
      open={open}
      onClose={close}
      title="Notifications"
      subtitle={`${unread} non lue${unread > 1 ? "s" : ""}`}
      footer={
        <Button
          variant="secondary"
          size="sm"
          icon="check-check"
          onClick={markAllRead}
          disabled={unread === 0}
        >
          Tout marquer comme lu
        </Button>
      }
    >
      {loading && (
        <ul className="-mx-5 -my-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <NotifRowSkeleton key={i} />
          ))}
        </ul>
      )}

      {!loading && error && (
        <ErrorState
          title="Notifications indisponibles"
          description={error}
          retry={() => fetchList({ page: 1, unread_only: false })}
        />
      )}

      {!loading && !error && items.length === 0 && (
        <EmptyState
          icon="bell-off"
          title="Aucune notification"
          description="Vous serez notifié ici des événements de vos e-factures et livraisons."
        />
      )}

      {!loading && !error && items.length > 0 && (
        <ul className="-mx-5 -my-4">
          {items.map((n) => {
            const meta = notifTypeMeta(n.type);
            const priorityTone = NOTIF_PRIORITY_TONE[n.priority] || meta.tone;
            return (
              <li key={n.id}>
                <button
                  type="button"
                  onClick={() => onRowClick(n)}
                  className={cn(
                    "w-full text-left px-5 py-3 border-b border-border-subtle border-l-[3px] hover:bg-surface-hover transition-colors",
                    TONE_BORDER[priorityTone] || "border-l-border-default",
                    !n.lue && "bg-accent-soft/40",
                  )}
                >
                  <div className="flex items-start gap-3">
                    <Icon
                      name={meta.icon}
                      size={16}
                      className={cn("mt-0.5 shrink-0", TONE_TEXT[meta.tone] || "text-text-muted")}
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-[13px] font-semibold text-text-strong truncate">
                          {n.titre || meta.label}
                        </span>
                        {!n.lue && (
                          <span className="w-1.5 h-1.5 rounded-full bg-accent shrink-0" />
                        )}
                      </div>
                      {n.message && (
                        <p className="text-xs text-text-default leading-snug mt-0.5">
                          {n.message}
                        </p>
                      )}
                      <p className="text-2xs text-text-muted font-mono mt-0.5">
                        {fmtRelTime(n.created_at)}
                      </p>
                    </div>
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </Drawer>
  );
}
